import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Gift, 
  Star, 
  ShoppingBag, 
  TreePine, 
  Heart,
  Zap,
  Filter
} from 'lucide-react';

export default function Rewards() {
  const [filter, setFilter] = useState('all');

  const rewards = [
    {
      id: 1,
      title: '20% Off Eco-Friendly Products',
      description: 'Get 20% discount at EcoShop for sustainable products',
      cost: 500,
      category: 'discount',
      image: 'https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
      icon: ShoppingBag,
      available: 50,
      claimed: false
    },
    {
      id: 2,
      title: 'Plant 5 Trees',
      description: "We'll donate to plant 5 trees in your name through our partner organizations",
      cost: 1000,
      category: 'donation',
      image: 'https://images.pexels.com/photos/1072824/pexels-photo-1072824.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
      icon: TreePine,
      available: 25,
      claimed: false
    },
    {
      id: 3,
      title: 'Eco Champion Badge',
      description: 'Exclusive digital badge to showcase your environmental commitment',
      cost: 300,
      category: 'badge',
      image: 'https://images.pexels.com/photos/1037999/pexels-photo-1037999.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
      icon: Star,
      available: 100,
      claimed: true
    },
    {
      id: 4,
      title: 'Clean Energy Donation',
      description: 'Support renewable energy projects with a $10 donation',
      cost: 750,
      category: 'donation',
      image: 'https://images.pexels.com/photos/2800832/pexels-photo-2800832.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
      icon: Zap,
      available: 30,
      claimed: false
    },
    {
      id: 5,
      title: 'Wildlife Conservation Support',
      description: 'Contribute $15 to wildlife conservation efforts',
      cost: 1200,
      category: 'donation',
      image: 'https://images.pexels.com/photos/247431/pexels-photo-247431.jpeg?auto=compress&cs=tinysrgb&w=300&h=200&fit=crop',
      icon: Heart,
      available: 15,
      claimed: false
    }
  ];

  const categories = ['all', 'discount', 'donation', 'badge', 'experience'];
  const userEcoCoins = 1247;

  const filteredRewards = rewards.filter(reward => 
    filter === 'all' || reward.category === filter
  );

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'discount': return 'bg-blue-100 text-blue-700';
      case 'donation': return 'bg-green-100 text-green-700';
      case 'badge': return 'bg-purple-100 text-purple-700';
      case 'experience': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="h-full mobile-scroll custom-scrollbar">
      <div className="p-4 space-y-4 pb-6">
        {/* Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-6 text-white card-shadow"
        >
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="w-3 h-3 bg-yellow-300 rounded-full"></div>
              <span className="text-emerald-100">Your Balance</span>
            </div>
            <p className="text-4xl font-bold mb-2">{userEcoCoins.toLocaleString()}</p>
            <p className="text-emerald-100">EcoCoins Available</p>
          </div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="flex space-x-2 overflow-x-auto pb-2"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-4 py-2 rounded-full whitespace-nowrap font-medium transition-colors ${
                filter === category
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              {category === 'all' ? 'All Rewards' : category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </motion.div>

        {/* Rewards Grid */}
        <div className="space-y-4">
          {filteredRewards.map((reward, index) => {
            const Icon = reward.icon;
            const canAfford = userEcoCoins >= reward.cost;
            
            return (
              <motion.div
                key={reward.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                className={`bg-white rounded-2xl overflow-hidden card-shadow ${
                  reward.claimed ? 'opacity-75' : ''
                }`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={reward.image}
                    alt={reward.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(reward.category)}`}>
                      {reward.category}
                    </span>
                  </div>
                  {reward.claimed && (
                    <div className="absolute inset-0 bg-emerald-500 bg-opacity-90 flex items-center justify-center">
                      <div className="text-center text-white">
                        <Gift className="h-12 w-12 mx-auto mb-2" />
                        <p className="font-bold">Claimed!</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="bg-emerald-100 p-2 rounded-xl">
                      <Icon className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-2">{reward.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{reward.description}</p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-lg font-bold text-gray-900">
                          {reward.cost.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {reward.available} available
                      </p>
                    </div>
                    
                    {reward.claimed ? (
                      <div className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-xl font-medium">
                        Claimed ✓
                      </div>
                    ) : (
                      <button
                        disabled={!canAfford}
                        className={`px-4 py-2 rounded-xl font-medium transition-colors ripple ${
                          canAfford
                            ? 'bg-emerald-500 text-white'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {canAfford ? 'Claim Reward' : 'Not Enough Coins'}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredRewards.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="text-center py-12"
          >
            <div className="text-6xl mb-4">🎁</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No rewards found</h3>
            <p className="text-gray-600">Try a different category</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}